import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RolesService } from '../../../services/RolesService';

@Component({
  selector: 'app-roles-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './roles-panel.component.html',
  styleUrls: ['./roles-panel.component.css']
})
export class RolesPanelComponent implements OnChanges {
  @Input() mode!: 'user' | 'client';
  @Input() targetId!: string;
  @Input() readonly = false;

  availableRoles: string[] = [];
  activeRoles = new Set<string>();
  toggling = new Set<string>();

  constructor(private roles: RolesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['targetId'] || changes['mode']) && this.targetId && this.mode) {
      this.load();
    }
  }

  private load(): void {
    const onAvailable = (available: string[]) => {
      this.availableRoles = available;
      const onActive = (active: string[]) => {
        this.activeRoles = new Set(active);
      };
      if (this.mode === 'user') {
        this.roles.getUserRoles(this.targetId, onActive);
      } else {
        this.roles.getClientRoles(this.targetId, onActive);
      }
    };

    if (this.mode === 'user') {
      this.roles.getAvailableUserRoles(onAvailable);
    } else {
      this.roles.getAvailableClientRoles(onAvailable);
    }
  }

  isActive(role: string): boolean {
    return this.activeRoles.has(role);
  }

  isToggling(role: string): boolean {
    return this.toggling.has(role);
  }

  toggle(role: string): void {
    if (this.readonly || this.toggling.has(role)) return;
    this.toggling.add(role);

    const done = () => {
      this.toggling.delete(role);
    };

    if (this.isActive(role)) {
      const onSuccess = () => {
        this.activeRoles.delete(role);
        done();
      };
      if (this.mode === 'user') {
        this.roles.removeUserRole(this.targetId, role, onSuccess);
      } else {
        this.roles.removeClientRole(this.targetId, role, onSuccess);
      }
    } else {
      const onSuccess = () => {
        this.activeRoles.add(role);
        done();
      };
      if (this.mode === 'user') {
        this.roles.addUserRole(this.targetId, role, onSuccess);
      } else {
        this.roles.addClientRole(this.targetId, role, onSuccess);
      }
    }
  }

  labelFor(role: string): string {
    return role.replace(/([A-Z])/g, ' $1').replace(/^\./, '').trim();
  }

  groupFor(role: string): string {
    const dot = role.indexOf('.');
    return dot === -1 ? 'General' : role.substring(0, dot);
  }

  get grouped(): { group: string; roles: string[] }[] {
    const map = new Map<string, string[]>();
    for (const r of this.availableRoles) {
      const g = this.groupFor(r);
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(r);
    }
    return Array.from(map.entries()).map(([group, roles]) => ({ group, roles }));
  }
}
